import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react"
import { createPortal } from "react-dom"

type PortalSlotContextValue = {
  /** Debug / identity name for this slot instance. */
  name: string
  node: HTMLElement | null
  setNode: (node: HTMLElement | null) => void
}

export type PortalSlot = {
  /** Slot identity — for debugging and for naming a future keyed registry. */
  name: string
  /**
   * Owns the host node for this slot. Descendants call `useHostRef` /
   * `useTarget` / `Portal` against this provider.
   */
  Provider: (props: { children: React.ReactNode }) => React.ReactElement
  /**
   * Callback ref for the DOM host. Must be used under `Provider`.
   * Attaching / clearing the ref publishes the node to consumers.
   */
  useHostRef: () => (node: HTMLElement | null) => void
  /** Current host node, or null when unset / outside a Provider. */
  useTarget: () => HTMLElement | null
  /**
   * Renders children into the host when present; otherwise in place.
   * Safe outside a Provider (always in-place).
   */
  Portal: (props: { children: React.ReactNode }) => React.ReactElement
}

/**
 * A single named portal target: one host DOM node, many portaled children.
 *
 * One slot per provider instance. Nesting two different slots is composition
 * (`footer.Provider` around `nav.Provider`). A second provider for the *same*
 * slot shadows the outer host for descendants.
 *
 * ## Extending to multiple targets in one tree
 *
 * When a surface needs several hosts at once (`footer` + `nav`, etc.), prefer
 * composing slot providers first:
 *
 * ```tsx
 * <footerSlot.Provider>
 *   <navSlot.Provider>
 *     ...
 *     <header ref={navSlot.useHostRef()} />
 *     <footer ref={footerSlot.useHostRef()} />
 *   </navSlot.Provider>
 * </footerSlot.Provider>
 * ```
 *
 * If that gets unwieldy — many keys, or hosts that should fall through to a
 * parent when the local tree does not define them — grow this into a keyed
 * registry:
 *
 * - One context value: `Map<string, HTMLElement | null>` (or get/set by key)
 * - `useHostRef(key)` / `useTarget(key)` / `<Portal slot={key}>`
 * - Unrecognized keys ask the parent context (nested providers each own a
 *   subset; miss → parent). Decide explicitly whether an empty local
 *   registration shadows the parent or passes through.
 * - Keep `name` (or a typed key union) so callers do not rely on free strings.
 *
 * Do not add that registry until a second real multi-host use appears; the
 * factory below is the reusable unit until then.
 */
export function createPortalSlot(name: string): PortalSlot {
  const SlotContext = createContext<PortalSlotContextValue | null>(null)

  function Provider({ children }: { children: React.ReactNode }) {
    const [node, setNodeState] = useState<HTMLElement | null>(null)
    const setNode = useCallback((next: HTMLElement | null) => {
      setNodeState(next)
    }, [])
    const value = useMemo(
      () => ({ name, node, setNode }),
      [name, node, setNode]
    )

    return (
      <SlotContext.Provider value={value}>{children}</SlotContext.Provider>
    )
  }

  function useHostRef() {
    const context = useContext(SlotContext)
    if (!context) {
      throw new Error(
        `createPortalSlot("${name}").useHostRef() requires a Provider`
      )
    }
    return context.setNode
  }

  function useTarget() {
    return useContext(SlotContext)?.node ?? null
  }

  function Portal({ children }: { children: React.ReactNode }) {
    const target = useTarget()
    return target ? createPortal(children, target) : <>{children}</>
  }

  return {
    name,
    Provider,
    useHostRef,
    useTarget,
    Portal
  }
}
